import { create } from "zustand";
import { devtools } from "zustand/middleware";
import { TRequestStatus } from "@models/msc.model";
import { TLeadStatus, TLeadStatusChanges } from "@/models/leads.model";
import leadsService from "@/services/leads.service";

type LeadStatusState = {
  initialized: boolean;
  status: TRequestStatus;
  list: TLeadStatus[];
};

type LeadStatusActions = {
  getLeadStatusList: () => Promise<TLeadStatus[]>;
  reloadLeadStatusList: () => Promise<TLeadStatus[]>;
  saveLeadStatus: (data: Pick<TLeadStatus, "name" | "color">) => Promise<TLeadStatus>;
  updateLeadStatus: (
    id: number,
    changes: TLeadStatusChanges
  ) => Promise<TLeadStatus | TLeadStatus[]>;
  deleteLeadStatus: (id: number, newStatusId?: number) => Promise<TLeadStatus[]>;
  updateTotalLeads: (data: { oldStatus: number; newStatus: number }) => void;
};

export const useLeadStatusStore = create<LeadStatusState & LeadStatusActions>()(
  devtools(
    (set, get) => ({
      initialized: false,
      status: "IDLE",
      list: [],

      getLeadStatusList: async () => {
        const { list, initialized } = get();

        // if already cached, return directly
        if (initialized) return list;

        set({ status: "PENDING" }, false, "getLeadStatusList/pending");
        const response = await leadsService.getLeadStatusList();
        set(
          { list: response, status: "IDLE", initialized: true },
          false,
          "getLeadStatusList/fulfilled"
        );
        return response;
      },

      /**
       * Force-refetches the catalog, bypassing the `initialized` cache. Needed after
       * a reorder or a delete: both rewrite the `order` of every remaining status
       * server-side, and a delete moves lead counts onto the reassignment target.
       */
      reloadLeadStatusList: async () => {
        const response = await leadsService.getLeadStatusList();
        set(
          { list: response, status: "IDLE", initialized: true },
          false,
          "reloadLeadStatusList/fulfilled"
        );
        return response;
      },

      saveLeadStatus: async (data) => {
        const response = await leadsService.saveLeadStatus(data);
        set((state) => ({ list: [...state.list, response] }), false, "saveLeadStatus/fulfilled");
        return response;
      },

      updateLeadStatus: async (id, changes) => {
        const response = await leadsService.updateLeadStatus({ id, ...changes });
        // A reorder rewrites every status' `order`, so the single-item patch isn't
        // enough — pull the whole list back instead.
        if (changes.order !== undefined) return get().reloadLeadStatusList();

        set(
          (state) => ({
            list: state.list.map((current) => {
              if (current.id !== response.id) return current;
              // PATCH /lead/status/:id answers with the bare row, without the computed
              // `totalLeads`. Merging (instead of replacing) keeps the count we already
              // have — a rename or recolor doesn't move any lead.
              return {
                ...current,
                ...response,
                totalLeads: response.totalLeads ?? current.totalLeads,
              };
            }),
          }),
          false,
          "updateLeadStatus/fulfilled"
        );
        return response;
      },

      deleteLeadStatus: async (id, newStatusId) => {
        await leadsService.deleteLeadStatus(id, newStatusId);
        set(
          (state) => ({ list: state.list.filter((status) => status.id !== id) }),
          false,
          "deleteLeadStatus/fulfilled"
        );
        // Reassigned leads changed the counts and the backend resequenced `order`.
        return get().reloadLeadStatusList();
      },

      updateTotalLeads: (data) =>
        set(
          (state) => ({
            list: state.list.map((item) => {
              if (item.id === data.oldStatus) return { ...item, totalLeads: item.totalLeads - 1 };
              if (item.id === data.newStatus) return { ...item, totalLeads: item.totalLeads + 1 };
              return item;
            }),
          }),
          false,
          "updateTotalLeads"
        ),
    }),
    { name: "leadStatusList" }
  )
);
